import { UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconSelector } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SortDirection, StockSort } from './inventory.js';
import classes from './Materials.module.css';
import type { MaterialSort } from './types.js';

/** Tablonun sırası: sütun ve yön. */
export interface SortState<C extends string> {
  column: C;
  direction: SortDirection;
}

export type StockSortState = SortState<StockSort>;
export type MaterialSortState = SortState<MaterialSort>;

/** Varsayılan sıra: kategori (sunucunun sırası). */
export const DEFAULT_STOCK_SORT: StockSortState = { column: 'category', direction: 'asc' };

/** Material Master varsayılanı: kategori sırası; görünen sütunların hiçbiri etkin değil. */
export const DEFAULT_MATERIAL_SORT: MaterialSortState = { column: 'category', direction: 'asc' };

/** Başlığa tıklanınca: aynı sütunda yön değişir, başka sütunda artan başlar (plan §0.6). */
export function nextSort<C extends string>(current: SortState<C>, column: C): SortState<C> {
  return current.column === column
    ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { column, direction: 'asc' };
}

/**
 * Sayfalı listenin sırası sunucuda verilir (M6f G5-15): varsayılan sıra sorguya yazılmaz, diğerleri
 * sütun ve yön olarak gider.
 */
export function serverSort<C extends string>(
  sort: SortState<C>,
  fallback: SortState<C>,
): { sort?: C; direction?: SortDirection } {
  return sort.column === fallback.column && sort.direction === fallback.direction
    ? {}
    : { sort: sort.column, direction: sort.direction };
}

/**
 * Sıralanabilir sütun başlığı (CLAUDE.md "tablolar sıralanabilir"; plan §0.6): `aria-sort`
 * `<th>`'de, yön iki tıkla değişir; sıralanmamış sütunda soluk çift ok, sıralı sütunda yön oku.
 */
export function SortTh<C extends string>({
  column,
  label,
  sort,
  onSort,
  numeric = false,
  className,
  col,
}: {
  column: C;
  label: string;
  sort: SortState<C>;
  onSort: (sort: SortState<C>) => void;
  numeric?: boolean;
  className?: string;
  /** Dar kapta gizlenecek sütunun adı (`data-col`). */
  col?: string | undefined;
}) {
  const { t } = useTranslation();
  const active = sort.column === column;
  let icon: ReactNode = <IconSelector size={13} aria-hidden className={classes.sortIdle} />;
  if (active) {
    icon =
      sort.direction === 'asc' ? (
        <IconChevronUp size={13} aria-hidden />
      ) : (
        <IconChevronDown size={13} aria-hidden />
      );
  }
  return (
    <th
      scope="col"
      className={className}
      data-numeric={numeric || undefined}
      data-col={col}
      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <UnstyledButton
        className={classes.sort}
        data-active={active || undefined}
        onClick={() => onSort(nextSort(sort, column))}
        aria-label={t('stock.sortBy', { column: label })}
      >
        <span>{label}</span>
        {icon}
      </UnstyledButton>
    </th>
  );
}
